import React, { useDeferredValue, useMemo, useState } from 'react';
import { parseChartText, formatKeySignature, formatSectionRepeat, type ChartTextImportResult } from '@chordbaker/music-theory';
import { useTheme, Theme } from '../contexts/ThemeContext';
import { CONSTANTS } from '../constants';
import { generateUniqueId } from '../utils/idUtils';
import CloseIcon from './icons/CloseIcon';
import InfoIcon from './icons/InfoIcon';

// RN-ALIGNED: mirrors react-native/src/components/PasteChartModal.tsx
const createStyles = (theme: Theme) => ({
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: theme.colors.modalOverlay,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
    zIndex: 50,
  },
  card: {
    backgroundColor: theme.colors.modalBackground,
    borderRadius: theme.radii.lg,
    boxShadow: theme.shadows.modal,
    width: '100%',
    maxWidth: 560,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.lg,
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  title: {
    margin: 0,
    fontSize: theme.typography.sizes.xl,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
  },
  closeButton: {
    display: 'flex',
    padding: theme.spacing.xs,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: theme.colors.textSecondary,
  },
  content: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing.md,
    padding: theme.spacing.lg,
    overflowY: 'auto' as const,
  },
  intro: {
    margin: 0,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  textarea: {
    width: '100%',
    minHeight: 200,
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
    padding: theme.spacing.md,
    borderRadius: theme.radii.md,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.isDark ? theme.colors.surfaceSecondary : theme.colors.surface,
    color: theme.colors.text,
    // Chord columns only line up over lyrics in a monospace font
    fontFamily: theme.typography.families.mono,
    fontSize: theme.typography.sizes.sm,
    lineHeight: theme.typography.lineHeights.normal,
    whiteSpace: 'pre' as const,
    outline: 'none',
  },
  notice: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
    padding: theme.spacing.md,
    borderRadius: theme.radii.md,
    backgroundColor: theme.colors.surfaceSecondary,
  },
  noticeText: {
    margin: 0,
    fontSize: theme.typography.sizes.sm,
    lineHeight: theme.typography.lineHeights.normal,
    color: theme.colors.textSecondary,
  },
  previewHeading: {
    margin: 0,
    fontSize: theme.typography.sizes.sm,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
  },
  previewMeta: {
    margin: 0,
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  previewEmpty: {
    margin: 0,
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textTertiary,
  },
  sectionList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  sectionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing.md,
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    fontSize: theme.typography.sizes.sm,
  },
  sectionTitle: {
    color: theme.colors.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  sectionTitleUntitled: {
    fontStyle: 'italic',
    color: theme.colors.textSecondary,
  },
  sectionCounts: {
    flexShrink: 0,
    color: theme.colors.textSecondary,
  },
  footer: {
    display: 'flex',
    gap: theme.spacing.md,
    padding: theme.spacing.lg,
    borderTop: `1px solid ${theme.colors.border}`,
  },
  button: {
    flex: 1,
    minHeight: theme.sizes.hitTarget,
    borderRadius: theme.radii.md,
    border: 'none',
    cursor: 'pointer',
    fontSize: theme.typography.sizes.md,
    fontWeight: theme.typography.weights.semiBold,
    transition: `opacity ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  cancelButton: {
    backgroundColor: theme.colors.surfaceSecondary,
    color: theme.colors.textSecondary,
  },
  importButton: {
    backgroundColor: theme.colors.primary,
    color: theme.colors.textInverse,
  },
  buttonDisabled: {
    opacity: theme.opacity.disabled,
    cursor: 'not-allowed',
  },
});

const plural = (count: number, singular: string, pluralForm: string) => `${count} ${count === 1 ? singular : pluralForm}`;

interface PasteChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Receives the parsed chart; the caller loads `result.song`. */
  onImport: (result: ChartTextImportResult) => void;
}

/**
 * Paste plain text (chords over lyrics, or ChordPro) and import it as a chart.
 * Parsing is the shared, deterministic parseChartText — no network involved.
 */
const PasteChartModal: React.FC<PasteChartModalProps> = ({ isOpen, onClose, onImport }) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [text, setText] = useState('');
  // Keeps typing responsive on long pastes: the preview catches up after.
  const deferredText = useDeferredValue(text);
  const preview = useMemo(
    () => parseChartText(deferredText, { lyricsMaxChars: CONSTANTS.LYRICS_MAX_CHARS }),
    [deferredText],
  );
  const canImport = text.trim() !== '' && preview.counts.slots > 0;

  const handleClose = () => {
    setText('');
    onClose();
  };

  const handleImport = () => {
    if (!canImport) return;
    // Parse again with the app's id generator, so ids can't collide with
    // ones created later in the chart.
    const result = parseChartText(text, {
      lyricsMaxChars: CONSTANTS.LYRICS_MAX_CHARS,
      generateId: generateUniqueId,
    });
    setText('');
    onImport(result);
  };

  if (!isOpen) return null;

  // Only a key the text states ({key:} / "Key:"); it is never guessed
  const keyText = preview.song.key ? `Key: ${formatKeySignature(preview.song.key)}` : 'No key detected';

  const renderPreview = () => {
    if (deferredText.trim() === '') {
      return <p style={styles.previewEmpty}>Paste a chart to see what will be imported.</p>;
    }
    if (preview.counts.slots === 0) {
      return <p style={styles.previewEmpty}>No chords or lyrics found in this text.</p>;
    }
    return (
      <>
        <p style={styles.previewMeta}>
          {preview.format === 'chordpro' ? 'ChordPro' : 'Chords over lyrics'} · {keyText}
        </p>
        <ul style={styles.sectionList} aria-label="Detected sections">
          {preview.sections.map((section, index) => (
            <li key={index} style={styles.sectionRow}>
              <span style={{ ...styles.sectionTitle, ...(section.title ? {} : styles.sectionTitleUntitled) }}>
                {section.title || 'Untitled section'}
                {section.repeat !== null && ` ${formatSectionRepeat(section.repeat)}`}
              </span>
              <span style={styles.sectionCounts}>
                {plural(section.rows, 'row', 'rows')} · {plural(section.slots, 'slot', 'slots')}
              </span>
            </li>
          ))}
        </ul>
        {preview.skippedLines > 0 && (
          <p style={styles.previewMeta}>
            {plural(preview.skippedLines, 'line', 'lines')} left out (tabs, chord diagrams or song info)
          </p>
        )}
      </>
    );
  };

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div
        style={styles.card}
        role="dialog"
        aria-modal="true"
        aria-labelledby="paste-chart-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.key === 'Escape' && handleClose()}
      >
        <div style={styles.header}>
          <h2 id="paste-chart-title" style={styles.title}>Paste Chart</h2>
          <button onClick={handleClose} style={styles.closeButton} aria-label="Close">
            <CloseIcon size={20} color="currentColor" />
          </button>
        </div>

        <div style={styles.content}>
          <p style={styles.intro}>Paste a chart with chords above the lyrics, or in ChordPro format.</p>
          <textarea
            style={styles.textarea}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={CONSTANTS.PASTE_IMPORT_PLACEHOLDER}
            aria-label="Chart text"
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            autoFocus
          />
          <div style={styles.notice} role="note">
            <InfoIcon size={16} color={theme.colors.textSecondary} />
            <p style={styles.noticeText}>{CONSTANTS.PASTE_IMPORT_NOTICE}</p>
          </div>
          <h3 style={styles.previewHeading}>Preview</h3>
          {renderPreview()}
        </div>

        <div style={styles.footer}>
          <button onClick={handleClose} style={{ ...styles.button, ...styles.cancelButton }}>
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!canImport}
            style={{ ...styles.button, ...styles.importButton, ...(canImport ? {} : styles.buttonDisabled) }}
          >
            Import
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasteChartModal;
